import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { CrmOption } from '../../services/api/crmApi';
import { CrmFilters, CrmSourceKey } from './crmSources';
import { useCrmOptions } from './useCrmOptions';

export interface CrmAutocompleteProps {
  tenantId: string | undefined;
  source: CrmSourceKey | string | undefined;
  filters?: CrmFilters;
  /** Selected option id. */
  value: string | null;
  onChange: (id: string | null, option: CrmOption | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  /** false skips the CRM request (e.g. on the builder canvas). */
  enabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}

/**
 * Searchable single-select over a CRM list (segments, channels, ...).
 * Used both as a form field and inside builder settings.
 */
export const CrmAutocomplete: React.FC<CrmAutocompleteProps> = ({
  tenantId,
  source,
  filters,
  value,
  onChange,
  placeholder,
  label,
  disabled,
  required,
  enabled = true,
  error,
  helperText,
  size = 'small',
}) => {
  const { options, loading, error: loadFailed, source: def } = useCrmOptions(
    tenantId,
    source,
    filters,
    enabled,
  );

  // Show a saved id immediately, even before (or without) its option loading.
  const selected: CrmOption | null = value
    ? options.find((option) => option.id === value) || { id: value, label: value }
    : null;

  return (
    <Autocomplete<CrmOption>
      options={options}
      value={selected}
      loading={loading}
      disabled={disabled}
      size={size}
      fullWidth
      autoHighlight
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      onChange={(_, option) => onChange(option?.id ?? null, option)}
      loadingText="Loading…"
      noOptionsText={loadFailed ? `Could not load ${def.noun}` : `No ${def.noun} found`}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder || `Select ${def.label.toLowerCase()}`}
          error={error || loadFailed}
          helperText={loadFailed ? `Could not load ${def.noun}` : helperText}
        />
      )}
    />
  );
};

export default CrmAutocomplete;
